import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { usePermissions } from '../contexts/PermissionsContext';
import axios from 'axios';
import {
  LayoutDashboard, FilePlus, FileText, CheckSquare, Wallet,
  BarChart3, Users, LogOut, Building2, Plane, Menu, X,
  Zap, Download, Globe, Home, BookOpen, Key, Eye, EyeOff, AlertCircle, Settings, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Sidebar = () => {
  const { user, logout } = useAuth();
  const { activeModule, switchModule, isDomestic } = useModule();
  const { hasPage, hasModule } = usePermissions();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(''); setPwSuccess('');
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError('New passwords do not match');
      return;
    }
    setPwLoading(true);
    try {
      const res = await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password
      });
      setPwSuccess(res.data.message);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => { setShowChangePassword(false); setPwSuccess(''); }, 1500);
    } catch (err) {
      setPwError(err.response?.data?.detail || 'Failed to change password');
    } finally { setPwLoading(false); }
  };

  const role = user?.role;
  // Accounts only sees International, DGM/senior_approver only sees Domestic
  const forcedModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : null;
  const hasDomestic = hasModule('domestic_access');
  const hasInternational = hasModule('international_access');
  const showModuleSwitcher = !forcedModule && hasDomestic && hasInternational;

  const getNavItems = () => {
    // All possible nav items mapped to permission keys
    const allItems = [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', permKey: 'dashboard' },
      { path: '/trips', icon: Plane, label: role === 'bdm' ? 'My Trips' : role === 'accounts' ? 'Intl Trips' : (role === 'dgm' || role === 'senior_approver') ? 'Domestic Trips' : 'Trips', permKey: 'trips' },
      { path: '/expenses/new', icon: FilePlus, label: 'New Expense', permKey: 'new_expense' },
      { path: '/expenses/quick-add', icon: Zap, label: 'Quick Add', permKey: 'quick_add' },
      { path: '/expenses/my-requests', icon: FileText, label: 'My Requests', permKey: 'my_requests' },
      { path: '/expenses/pending-approval', icon: CheckSquare, label: 'Pending Approval', permKey: 'pending_approval' },
      { path: '/expenses/pending-payment', icon: Wallet, label: 'Reimbursements', permKey: 'reimbursements' },
      { path: '/expenses/all', icon: BarChart3, label: role === 'accounts' ? 'Intl Expenses' : 'All Expenses', permKey: 'all_expenses' },
      { path: '/ledger', icon: BookOpen, label: 'Company Ledger', permKey: 'company_ledger' },
      { path: '/export', icon: Download, label: 'Export', permKey: 'export' },
      { path: '/users', icon: Users, label: 'User Management', permKey: 'user_management' },
      { path: '/permissions', icon: Shield, label: 'Permissions', permKey: 'permissions' },
      { path: '/settings', icon: Settings, label: 'Settings', permKey: 'settings' },
    ];

    return allItems.filter(item => hasPage(item.permKey));
  };

  const navItems = getNavItems();
  const roleLabels = { bdm: 'BDM', dgm: 'DGM', hr: 'HR', admin: 'Admin', management: 'Management', accounts: 'Accounts', senior_approver: 'Senior Approver' };
  const roleLabel = roleLabels[role] || role;

  const navContent = (
    <>
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4.5 h-4.5 text-white" strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>Panelart Decor</div>
            <div className="text-[10px] text-gray-400 font-medium tracking-wide uppercase">Field Expense System</div>
          </div>
        </div>
      </div>

      {/* Module Switcher */}
      {showModuleSwitcher && (
        <div className="px-3 pt-3 pb-1" data-testid="module-switcher">
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => switchModule('DOMESTIC')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-semibold transition-all ${
                (forcedModule || activeModule) === 'DOMESTIC'
                  ? 'bg-white text-indigo-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="module-domestic-btn"
            >
              <Home className="w-3.5 h-3.5" />
              Domestic
            </button>
            <button
              onClick={() => switchModule('INTERNATIONAL')}
              className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-xs font-semibold transition-all ${
                (forcedModule || activeModule) === 'INTERNATIONAL'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid="module-international-btn"
            >
              <Globe className="w-3.5 h-3.5" />
              International
            </button>
          </div>
        </div>
      )}

      {/* Forced/single module indicator */}
      {(forcedModule || (hasDomestic && !hasInternational) || (!hasDomestic && hasInternational)) && (
        <div className="px-4 pt-2">
          <div className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded ${
            (forcedModule || activeModule) === 'DOMESTIC' || (hasDomestic && !hasInternational) ? 'bg-indigo-50 text-indigo-600' : 'bg-emerald-50 text-emerald-600'
          }`}>
            {(forcedModule || activeModule) === 'DOMESTIC' || (hasDomestic && !hasInternational) ? 'Domestic Module' : 'International Module'}
          </div>
        </div>
      )}

      <nav className="flex-1 py-4 px-3 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${isActive ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <item.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />{item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-xs font-semibold text-gray-600">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-900 truncate">{user?.name}</div>
            <div className="text-xs text-gray-400">{roleLabel}</div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button onClick={() => { setShowChangePassword(true); setPwError(''); setPwSuccess(''); setPwForm({ current_password: '', new_password: '', confirm_password: '' }); }}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-indigo-600 transition-colors w-full px-1 py-1" data-testid="change-password-button">
            <Key className="w-4 h-4" />Change Password
          </button>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-500 hover:text-red-600 transition-colors w-full px-1 py-1" data-testid="logout-button">
            <LogOut className="w-4 h-4" />Sign Out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <button className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md border border-gray-200" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>
      {mobileOpen && <div className="md:hidden fixed inset-0 bg-black/30 z-30" onClick={() => setMobileOpen(false)} />}
      <div className={`sidebar flex flex-col ${mobileOpen ? 'open' : ''}`} data-testid="sidebar">{navContent}</div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Change Password</h2>
              <button onClick={() => setShowChangePassword(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleChangePassword} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
                <div className="relative">
                  <input type={showCurrentPw ? 'text' : 'password'} value={pwForm.current_password}
                    onChange={e => setPwForm({...pwForm, current_password: e.target.value})}
                    className="input-field pr-10" required data-testid="current-password-input" />
                  <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                <div className="relative">
                  <input type={showNewPw ? 'text' : 'password'} value={pwForm.new_password}
                    onChange={e => setPwForm({...pwForm, new_password: e.target.value})}
                    className="input-field pr-10" required minLength={8} placeholder="Min 8 chars (letters + numbers)"
                    data-testid="new-password-input" />
                  <button type="button" onClick={() => setShowNewPw(!showNewPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                <input type="password" value={pwForm.confirm_password}
                  onChange={e => setPwForm({...pwForm, confirm_password: e.target.value})}
                  className="input-field" required minLength={8} data-testid="confirm-password-input" />
              </div>
              {pwError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="change-password-error">
                  <p className="text-red-700 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" />{pwError}</p>
                </div>
              )}
              {pwSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg" data-testid="change-password-success">
                  <p className="text-green-700 text-sm">{pwSuccess}</p>
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <button type="submit" disabled={pwLoading} className="btn btn-primary flex-1 justify-center" data-testid="submit-change-password">
                  {pwLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button type="button" onClick={() => setShowChangePassword(false)} className="btn btn-secondary flex-1 justify-center">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
